import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { defaultColDef } from 'components/AgGrid/AgGridTable/helpers';
import { getRowId } from 'components/AgGrid/utils';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderChangeObjectVolumesTransferInput, WorkOrderChangeSeasonData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Loader } from 'uikit';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';
import type React from 'react';
import { useMemo, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { SeasonsSelectForm } from '../MoveVolumesBetweenSeasons/components/SeasonsSelectForm';

import { useMoveVolumesBetweenSeasonsForObjectColumns } from './hooks/ useMoveVolumesBetweenSeasonsForObjectColumns';
import { useUerMoveWorkOrderChangeObjectReminders } from './hooks/useUerMoveWorkOrderChangeObjectReminders';
import { useUerWorkOrderChangeObjectsForReminders } from './hooks/useUerWorkOrderChangeObjectsForReminders';

import Style from './MoveVolumesBetweenSeasonsForObject.module.css';

interface IMoveVolumesBetweenSeasonsForObject {
    onClose: () => void;
    refreshTableDataHandler: () => void;
    isOpen: boolean;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    workOrderChangeId: number;
    workOrderChangeRootObjectId?: number;
    workOrderRootObjectId?: number;
}

const title = 'Перенос объемов между сезонами для объекта';

export const MoveVolumesBetweenSeasonsForObject: React.FC<IMoveVolumesBetweenSeasonsForObject> = (props) => {
    const {
        onClose,
        isOpen,
        refreshTableDataHandler,
        workOrderChangeSeasons,
        workOrderChangeId,
        workOrderRootObjectId,
        workOrderChangeRootObjectId,
    } = props;

    const { handleSubmit, formState, control, setValue, setError, clearErrors } = useForm({
        mode: 'onChange',
    });

    const { status, items } = useUerWorkOrderChangeObjectsForReminders({
        isOpen,
        workOrderChangeId,
        workOrderRootObjectId,
        workOrderChangeRootObjectId,
    });

    const seasonFrom = useWatch({ control, name: 'seasonFrom' });
    const seasonTo = useWatch({ control, name: 'seasonTo' });

    const onCloseLocal = useCallback(() => {
        setValue('seasonFrom', null);
        setValue('seasonTo', null);

        onClose();
    }, [onClose, setValue]);

    const onSuccess = useCallback(() => {
        refreshTableDataHandler();
        onCloseLocal();
    }, [onCloseLocal, refreshTableDataHandler]);

    const { uerMoveWorkOrderChangeObjectRemindersStatus, uerMoveWorkOrderChangeObjectRemindersMakeRequest } =
        useUerMoveWorkOrderChangeObjectReminders({
            onSuccess,
        });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCloseLocal,
        });

    const volumesFieldsIds = useMemo(() => items.map((i) => `volume_${i.id}`), [items]);
    const volumes = useWatch({ control, name: volumesFieldsIds });
    const hasVolumeDataToMove = useMemo(() => volumes?.some((v) => !!v), [volumes]);
    const volumesWithErrors = volumesFieldsIds?.filter((id) => !!formState.errors[id]);
    const btnConfirmDisabled = !R.isEmpty(formState.errors) || !hasVolumeDataToMove || seasonFrom?.id === seasonTo?.id;

    const columns = useMoveVolumesBetweenSeasonsForObjectColumns({
        setError,
        setValue,
        clearErrors,
        volumesWithErrors,
        items,
        isOpen,
    });

    const onConfirm = useCallback(
        (formData) => {
            const input: WorkOrderChangeObjectVolumesTransferInput = {
                sourceSeasonId: seasonFrom?.id,
                targetSeasonId: seasonTo?.id,
                targetData: items
                    .filter((i) => formData[`volume_${i.id}`])
                    .map((i) => ({
                        transferPercentValue: replacerCost(formData[`volume_${i.id}`]),
                        workOrderObjectChangeId: i.workOrderObjectChangeId,
                        workOrderObjectId: i.workOrderObjectId,
                    })),
            };

            uerMoveWorkOrderChangeObjectRemindersMakeRequest(input);
        },
        [items, seasonFrom?.id, seasonTo?.id, uerMoveWorkOrderChangeObjectRemindersMakeRequest],
    );

    const gridOptions = {
        headerHeight: 40,
    };

    return (
        <>
            <ModalDialog
                title={title}
                hasOverlay
                withTitleCloseButton
                onClose={handleOpenConfirmationDialog}
                onConfirm={handleSubmit(onConfirm)}
                btnConfirmLabel="Перенести"
                btnCancelLabel="Отменить"
                btnConfirmDisabled={btnConfirmDisabled}
                bodyWidth={800}
                isCommit={uerMoveWorkOrderChangeObjectRemindersStatus === RequestStatus.FETCHING}
                isOpen={isOpen}
                isOnClickOutsideDisable
            >
                <div className={Style.moveVolumesBetweenSeasonsForObjectForm}>
                    <SeasonsSelectForm
                        control={control}
                        seasonTo={seasonTo}
                        seasonFrom={seasonFrom}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        setError={setError}
                        workOrderChangeSeasons={workOrderChangeSeasons}
                    />
                    <div className="values-block">
                        <div style={{ height: '100%' }} className="ag-theme-alpine WorkOrderChangePrice">
                            {status === RequestStatus.FETCHING ? (
                                <Loader />
                            ) : (
                                <AgGridTable
                                    columnDefs={columns}
                                    rowData={items}
                                    defaultColDef={defaultColDef}
                                    getRowId={getRowId}
                                    gridOptions={gridOptions}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </ModalDialog>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

MoveVolumesBetweenSeasonsForObject.displayName = 'MoveVolumesBetweenSeasonsForObject';
